import React from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

type Option = {
  id: string;
  label: string;
  color: string;
};

type CheckboxListProps = {
  title: string;
  options: Option[];
  value: string[];
  onChange: (selectedIds: string[]) => void;
};

export const CheckboxList: React.FC<CheckboxListProps> = ({
  title,
  options,
  value,
  onChange,
}) => {
  const allIds = options.map(o => o.id);
  const isAll = value.length === options.length;

  const handleToggle = (id: string) => {
    const isSelected = value.includes(id);
    const newSelected = isSelected ? value.filter(v => v !== id) : [...value, id];
    onChange(newSelected);
  };

  const handleToggleAll = () => {
    onChange(isAll ? [] : allIds);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center">
        <Tooltip title={isAll ? 'Deselect All' : 'Select All'}>
          <IconButton size="small" onClick={handleToggleAll}>
            {isAll ? <IndeterminateCheckBoxIcon /> : <CheckBoxIcon />}
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1">{title}</Typography>
      </Stack>

      <FormGroup sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
        {options.map(opt => {
          const isSelected = value.includes(opt.id);
          return (
            <FormControlLabel
              key={opt.id}
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={() => handleToggle(opt.id)}
                  sx={{ display: 'none' }}
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: opt.color,
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'grey.400',
                      mr: 1,
                    }}
                  />
                  <Typography variant="body1">{opt.label}</Typography>
                </Box>
              }
              sx={{ m: 0, py: 0.5 }}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};
